import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Eye, CheckCircle, RefreshCw } from "lucide-react";
import { BoltIcon } from "../components/Bolts";
import AdminNav from "../components/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOrdersPage = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [busy, setBusy] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/orders`);
            setOrders(res.data || []);
        } catch (e) {
            toast.error("Failed to load orders");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
    }, []);

    const deleteOrder = async (id) => {
        if (!window.confirm("Delete this order permanently?")) return;
        setBusy(true);
        try {
            await axios.delete(`${API}/orders/${id}`, {
                auth: { username: "admin", password: window.prompt("Admin password:") }
            });
            toast.success("Order deleted");
            reload();
            setSelectedOrder(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        } finally { setBusy(false); }
    };

    const markShipped = async (id) => {
        setBusy(true);
        try {
            await axios.post(`${API}/orders/${id}/mark-shipped`, {}, {
                auth: { username: "admin", password: window.prompt("Admin password:") }
            });
            toast.success("Marked as shipped");
            reload();
            setSelectedOrder(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-orders-page" className="min-h-[80vh]">
            <section className="border-b border-white/10 bg-storm-navy">
                <div className="container-x py-16 md:py-20 flex items-end justify-between flex-wrap gap-6">
                    <div>
                        <p className="eyebrow flex items-center gap-2 mb-4">
                            <BoltIcon className="w-3.5 h-3.5" /> Admin
                        </p>
                        <h1 className="text-4xl md:text-6xl text-white leading-[0.9]">
                            <span className="text-storm-bolt">Orders</span>
                        </h1>
                    </div>
                    <button
                        onClick={reload}
                        className="btn-secondary !text-sm !px-5 !py-3"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </button>
                </div>
            </section>

            <AdminNav active="orders" />

            <section className="container-x pt-10 pb-20 space-y-4">
                {loading ? (
                    <p className="text-storm-mist">Loading...</p>
                ) : orders.length === 0 ? (
                    <p className="text-storm-mist">No orders yet.</p>
                ) : (
                    <div className="overflow-x-auto border border-white/10">
                        <table className="w-full text-sm">
                            <thead className="bg-storm-void border-b border-white/10">
                                <tr>
                                    <th className="text-left px-4 py-3 text-storm-bolt uppercase tracking-wider font-heading text-xs">Date</th>
                                    <th className="text-left px-4 py-3 text-storm-bolt uppercase tracking-wider font-heading text-xs">Items</th>
                                    <th className="text-left px-4 py-3 text-storm-bolt uppercase tracking-wider font-heading text-xs">Total</th>
                                    <th className="text-left px-4 py-3 text-storm-bolt uppercase tracking-wider font-heading text-xs">Status</th>
                                    <th className="text-left px-4 py-3 text-storm-bolt uppercase tracking-wider font-heading text-xs">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map((ord) => (
                                    <tr key={ord.id} className="border-b border-white/10 hover:bg-storm-void/50 cursor-pointer" onClick={() => setSelectedOrder(ord)}>
                                        <td className="px-4 py-3 text-white">{new Date(ord.created_at).toLocaleDateString()}</td>
                                        <td className="px-4 py-3 text-storm-mist truncate max-w-[200px]">
                                            {ord.items?.map(it => it.title).join(", ") || "—"}
                                        </td>
                                        <td className="px-4 py-3 text-white font-heading">${(ord.total_cents / 100).toFixed(2)}</td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs uppercase px-2 py-1 border ${ord.status === "pending" ? "border-yellow-600/30 text-yellow-400" : "border-green-600/30 text-green-400"}`}>
                                                {ord.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
                                            <button onClick={() => setSelectedOrder(ord)} className="text-storm-bolt hover:text-white p-1" title="View details">
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => deleteOrder(ord.id)} disabled={busy} className="text-red-400 hover:text-red-300 p-1 disabled:opacity-50">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {selectedOrder && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedOrder(null)}>
                        <div className="bg-storm-navy border border-white/10 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-between items-start mb-6">
                                <h2 className="font-heading text-2xl text-white uppercase tracking-wider">Order {selectedOrder.id.slice(0, 8)}</h2>
                                <button onClick={() => setSelectedOrder(null)} className="text-storm-mist hover:text-white text-2xl">&times;</button>
                            </div>

                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-storm-mist text-xs uppercase mb-1">Date</p>
                                        <p className="text-white">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                                    </div>
                                    <div>
                                        <p className="text-storm-mist text-xs uppercase mb-1">Status</p>
                                        <p className="text-white capitalize">{selectedOrder.status}</p>
                                    </div>
                                    <div>
                                        <p className="text-storm-mist text-xs uppercase mb-1">Total</p>
                                        <p className="text-white font-heading text-lg">${(selectedOrder.total_cents / 100).toFixed(2)}</p>
                                    </div>
                                    <div>
                                        <p className="text-storm-mist text-xs uppercase mb-1">Shipping</p>
                                        <p className="text-white">${(selectedOrder.shipping_cents / 100).toFixed(2)}</p>
                                    </div>
                                </div>

                                <div className="border-t border-white/10 pt-4">
                                    <p className="text-storm-bolt text-sm uppercase tracking-wider mb-3 font-heading">Items</p>
                                    <div className="space-y-2">
                                        {selectedOrder.items?.map((it, i) => (
                                            <div key={i} className="flex justify-between text-sm">
                                                <span className="text-white">{it.qty}× {it.title}</span>
                                                <span className="text-storm-mist">${(it.priceCents * it.qty / 100).toFixed(2)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {selectedOrder.customer_email && (
                                    <div className="border-t border-white/10 pt-4">
                                        <p className="text-storm-mist text-xs uppercase mb-2">Customer</p>
                                        <p className="text-white">{selectedOrder.customer_name || "—"}</p>
                                        <p className="text-storm-mist text-sm">{selectedOrder.customer_email}</p>
                                    </div>
                                )}

                                {selectedOrder.promo_code && (
                                    <div className="border-t border-white/10 pt-4">
                                        <p className="text-storm-mist text-xs uppercase mb-2">Promo</p>
                                        <p className="text-storm-bolt font-heading">{selectedOrder.promo_code}</p>
                                        {selectedOrder.discount_cents > 0 && (
                                            <p className="text-sm text-storm-mist">-${(selectedOrder.discount_cents / 100).toFixed(2)}</p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="mt-6 flex gap-3 pt-6 border-t border-white/10">
                                {selectedOrder.status === "pending" && (
                                    <button onClick={() => markShipped(selectedOrder.id)} disabled={busy} className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 font-heading uppercase text-sm disabled:opacity-50">
                                        <CheckCircle className="w-4 h-4 inline mr-2" />
                                        Mark Shipped
                                    </button>
                                )}
                                <button onClick={() => deleteOrder(selectedOrder.id)} disabled={busy} className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 font-heading uppercase text-sm disabled:opacity-50">
                                    <Trash2 className="w-4 h-4 inline mr-2" />
                                    Delete
                                </button>
                                <button onClick={() => setSelectedOrder(null)} className="flex-1 bg-storm-void border border-white/10 text-white px-4 py-2 font-heading uppercase text-sm hover:bg-storm-navy">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};

export default AdminOrdersPage;
